'use client';

/**
 * useDebounce Hook
 *
 * Prevents rage clicking by debouncing function calls.
 * Waits for user to stop clicking before executing.
 *
 * @example
 * ```tsx
 * const handleSearch = useDebounce(
 *   async (query: string) => {
 *     await searchAPI(query);
 *   },
 *   500 // Wait 500ms after last keystroke
 * );
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebounce<T extends (...args: any[]) => any>(callback: T, delay = 1000): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    ((...args: Parameters<T>) => {
      // Clear previous timeout
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }) as T,
    [callback, delay],
  );
}

/**
 * useDebouncedValue Hook
 *
 * Debounces a value change (useful for search inputs).
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState('');
 * const debouncedSearch = useDebouncedValue(search, 500);
 *
 * // Only triggers API call 500ms after user stops typing
 * useEffect(() => {
 *   searchAPI(debouncedSearch);
 * }, [debouncedSearch]);
 * ```
 */
export function useDebouncedValue<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
